import { useQuery } from '@tanstack/react-query';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { feedApi } from '../api/feed.js';
import { storageApi } from '../api/storage.js';
import { PostCard } from '../components/PostCard.js';
import { PostComposer } from '../components/PostComposer.js';
import { StorageMeter } from '../components/StorageMeter.js';
import './FeedPage.css';

export function FeedPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [tag, setTag] = useState<string | undefined>(undefined);
  const [composerOpen, setComposerOpen] = useState(false);

  const feedQuery = useQuery({
    queryKey: ['feed', 'list', { page, search, tag }],
    queryFn: () => feedApi.list({ page, pageSize: 20, search: search || undefined, tag }),
  });

  const storageQuery = useQuery({
    queryKey: ['storage'],
    queryFn: storageApi.usage,
    refetchInterval: 120_000,
  });

  const allTags = Array.from(new Set(feedQuery.data?.items.flatMap((post) => post.tags) ?? []));

  return (
    <div>
      <div className="page-header feed-page-header">
        <div>
          <h1>{t('feed.title')}</h1>
          <p>{t('feed.subtitle')}</p>
        </div>
        {!composerOpen && (
          <button type="button" className="btn btn-primary" onClick={() => setComposerOpen(true)}>
            <Plus size={15} /> {t('feed.newPost')}
          </button>
        )}
      </div>

      {storageQuery.data && (storageQuery.data.isWarning || storageQuery.data.isFull) && (
        <p
          className={`feed-storage-alert ${storageQuery.data.isFull ? 'feed-storage-alert-full' : ''}`}
        >
          {storageQuery.data.isFull
            ? t('feed.storageFull')
            : t('feed.storageWarning', {
                percent: `${Math.round(storageQuery.data.ratio * 100)}%`,
              })}
        </p>
      )}

      {composerOpen && <PostComposer onDone={() => setComposerOpen(false)} />}

      <div className="feed-toolbar">
        <div className="feed-search">
          <Search size={15} />
          <input
            placeholder={t('feed.searchPlaceholder')}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>
        {storageQuery.data && <StorageMeter usage={storageQuery.data} />}
      </div>

      {allTags.length > 0 && (
        <div className="feed-tag-filter">
          <button
            type="button"
            className={`badge ${!tag ? 'feed-tag-active' : ''}`}
            onClick={() => setTag(undefined)}
          >
            {t('feed.allTags')}
          </button>
          {allTags.map((item) => (
            <button
              key={item}
              type="button"
              className={`badge ${tag === item ? 'feed-tag-active' : ''}`}
              onClick={() => setTag(item === tag ? undefined : item)}
            >
              #{item}
            </button>
          ))}
        </div>
      )}

      {feedQuery.isLoading && (
        <div className="center-state">
          <div className="spinner" />
        </div>
      )}

      {feedQuery.data?.items.length === 0 && <p className="center-state">{t('feed.noPosts')}</p>}

      {feedQuery.data?.items.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}

      {feedQuery.data?.hasMore && (
        <button
          type="button"
          className="btn feed-load-more"
          onClick={() => setPage((value) => value + 1)}
        >
          {t('feed.loadMore')}
        </button>
      )}
    </div>
  );
}
